import { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Share,
  Platform,
  Alert,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { X, Share2 } from 'lucide-react-native';
import { generateDuelInviteLink, generateDuelWebLink } from '@/lib/duelInvite';
import { getDuelShareText } from '@/lib/shareLinks';
import LiveDuelProgress from './LiveDuelProgress';
import DuelResultBanner from './DuelResultBanner';
import RematchButton from './RematchButton';
import type { Duel } from './DuelCard';
import { useUnits } from '@/lib/useUnits';
import { EXERCISE_CATALOG_FALLBACK, KNOWN_BODY_PARTS, resolveDuelExerciseNames } from '@/lib/exerciseCatalog';
import DuelShareCard from '@/components/share/DuelShareCard';
import { shareCard } from '@/lib/shareCard';

interface DuelDetailModalProps {
  duel: Duel | null;
  visible: boolean;
  onClose: () => void;
  currentUserId: string;
}

interface OpponentActivity {
  exercise_name: string;
  performed_at: string;
  exercise_count: number;
}

interface LiftEntry {
  id: string;
  profile_id: string;
  weight: number;
  reps: number;
  created_at: string;
}

export default function DuelDetailModal({ duel, visible, onClose, currentUserId }: DuelDetailModalProps) {
  const { theme } = useTheme();
  const router = useRouter();
  const { userUnits, displayWeight, unitLabel } = useUnits();
  const [lifts, setLifts] = useState<LiftEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [opponentName, setOpponentName] = useState('Opponent');
  const [opponentActivity, setOpponentActivity] = useState<OpponentActivity[]>([]);
  const [showDuelShareCard, setShowDuelShareCard] = useState(false);
  const duelCardRef = useRef<View>(null);
  // howToWin is now condensed inside LiveDuelProgress card

  const isChallenger = duel?.challenger_id === currentUserId;
  const opponentId = isChallenger ? duel?.challenged_id : duel?.challenger_id;
  const isCompleted = duel?.status === 'completed';
  const isActive = duel?.status === 'active';
  const isExpired = isActive && duel?.end_at ? new Date(duel.end_at).getTime() <= Date.now() : false;
  const isWinner = isCompleted && duel?.winner_user_id === currentUserId;
  const isLoss = isCompleted && duel?.winner_user_id != null && duel?.winner_user_id !== currentUserId;
  const isDraw = isCompleted && !duel?.winner_user_id;

  useEffect(() => {
    if (!visible || !duel) return;
    loadLifts();
    loadOpponentName();
    loadOpponentActivity();
  }, [visible, duel?.id]);

  // Poll opponent activity every 30s while active
  useEffect(() => {
    if (!visible || !isActive) return;
    const interval = setInterval(loadOpponentActivity, 30_000);
    return () => clearInterval(interval);
  }, [visible, isActive, duel?.id]);

  const loadOpponentName = async () => {
    if (!opponentId) return;
    const { data } = await supabase
      .from('profiles')
      .select('display_name, handle')
      .eq('id', opponentId)
      .single();
    if (data) setOpponentName(data.display_name || data.handle || 'Opponent');
  };

  const loadOpponentActivity = async () => {
    if (!opponentId || !duel) return;
    try {
      const since = duel.start_at || duel.created_at;
      const until = duel.end_at || new Date().toISOString();
      const { data } = await supabase
        .from('workouts')
        .select('performed_at, payload')
        .eq('user_id', opponentId)
        .gte('performed_at', since)
        .lte('performed_at', until)
        .order('performed_at', { ascending: false })
        .limit(10);

      if (!data) { setOpponentActivity([]); return; }

      const activities: OpponentActivity[] = data.map((w: any) => {
        let payload = w.payload;
        if (typeof payload === 'string') {
          try { payload = JSON.parse(payload); } catch { payload = {}; }
        }
        const exercises = payload?.exercises ?? [];
        const firstName = exercises[0]?.name || 'a workout';
        return {
          exercise_name: firstName,
          performed_at: w.performed_at,
          exercise_count: exercises.length,
        };
      });
      setOpponentActivity(activities);
    } catch {
      // silent
    }
  };

  const loadLifts = async () => {
    const exerciseNames = resolveDuelExerciseNames(duel?.exercise_name || duel?.exercise_id);
    if (!exerciseNames || exerciseNames.length === 0) { setLoading(false); return; }
    setLoading(true);
    try {
      const since = duel!.start_at || duel!.created_at;
      const until = duel!.end_at || new Date().toISOString();
      const { data } = await supabase
        .from('strength_events')
        .select('id, profile_id, weight, reps, created_at')
        .or(exerciseNames.map(n => `canonical_name.eq.${n},lift_key.eq.${n}`).join(','))
        .in('profile_id', [duel!.challenger_id, duel!.challenged_id])
        .gte('created_at', since)
        .lte('created_at', until)
        .order('created_at', { ascending: false });
      setLifts((data as LiftEntry[]) || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const margin = (() => {
    if (!isCompleted || duel?.challenger_score == null || duel?.challenged_score == null) return undefined;
    const diff = Math.abs(duel.challenger_score - duel.challenged_score);
    return diff > 0 ? `+${Math.round(diff)} ${userUnits}` : undefined;
  })();

  if (!duel) return null;

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        {/* Header */}
        <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
          <View style={{ flex: 1 }}>
            <Text style={[styles.title, { color: theme.colors.text }]}>
              {duel.exercise_name || 'Duel'}
            </Text>
            <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
              {(() => {
                const typeLabel = duel.duel_type === 'pr' ? 'PR Duel' : duel.duel_type === '1rm' ? '1RM Challenge' : duel.duel_type === 'strength' ? 'Strongest Set' : duel.duel_type === 'consistency' ? 'Consistency Check' : 'Volume Duel';
                const exName = duel.exercise_name ?? '';
                const match = exName ? EXERCISE_CATALOG_FALLBACK.find(e => e.name.toLowerCase() === exName.toLowerCase()) : null;
                // If exercise matched catalog, show its body_part; otherwise check if exercise_name itself IS a body part
                const bodyPart = match?.body_part
                  ? match.body_part
                  : KNOWN_BODY_PARTS.has(exName.toLowerCase()) ? exName.toLowerCase() : null;
                const formatted = bodyPart ? bodyPart.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;
                return formatted ? `${formatted} • ${typeLabel} • ${duel.timeframe_hours}h` : `${typeLabel} • ${duel.timeframe_hours}h`;
              })()}
            </Text>
          </View>
          <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
            <X size={24} color={theme.colors.text} />
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {/* Result banner for completed */}
          {isCompleted && (
            <DuelResultBanner
              isWinner={!!isWinner}
              isLoss={!!isLoss}
              isDraw={!!isDraw}
              opponentName={opponentName}
              margin={margin}
            />
          )}

          {/* Side-by-side score breakdown for completed duels */}
          {isCompleted && (duel?.challenger_score != null || duel?.challenged_score != null) && (() => {
            const myScore = isChallenger ? (duel?.challenger_score ?? 0) : (duel?.challenged_score ?? 0);
            const theirScore = isChallenger ? (duel?.challenged_score ?? 0) : (duel?.challenger_score ?? 0);
            const total = myScore + theirScore;
            const myPct = total > 0 ? (myScore / total) * 100 : 50;
            return (
              <View style={[styles.section, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
                <Text style={[styles.sectionTitle, { color: theme.colors.textSecondary }]}>FINAL BREAKDOWN</Text>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                  <View style={{ alignItems: 'center', flex: 1 }}>
                    <Text style={{ fontSize: 11, color: theme.colors.textSecondary, fontWeight: '700', marginBottom: 4 }}>YOU</Text>
                    <Text style={{ fontSize: 28, fontWeight: '900', color: isWinner ? theme.colors.accent : theme.colors.text }}>
                      {Math.round(displayWeight(myScore, 'lbs'))}
                    </Text>
                    <Text style={{ fontSize: 11, color: theme.colors.textTertiary }}>{unitLabel}</Text>
                  </View>
                  <View style={{ alignItems: 'center', justifyContent: 'center', paddingHorizontal: 12 }}>
                    <Text style={{ fontSize: 16, fontWeight: '900', color: theme.colors.textTertiary }}>VS</Text>
                  </View>
                  <View style={{ alignItems: 'center', flex: 1 }}>
                    <Text style={{ fontSize: 11, color: theme.colors.textSecondary, fontWeight: '700', marginBottom: 4 }}>{opponentName.toUpperCase()}</Text>
                    <Text style={{ fontSize: 28, fontWeight: '900', color: !isWinner && !isDraw ? theme.colors.accent : theme.colors.text }}>
                      {Math.round(displayWeight(theirScore, 'lbs'))}
                    </Text>
                    <Text style={{ fontSize: 11, color: theme.colors.textTertiary }}>{unitLabel}</Text>
                  </View>
                </View>
                {/* Tug-of-war bar */}
                <View style={{ height: 8, borderRadius: 4, backgroundColor: theme.colors.surfaceVariant, overflow: 'hidden', marginTop: 4 }}>
                  <View style={{ height: '100%', width: `${Math.max(4, Math.min(96, myPct))}%`, backgroundColor: theme.colors.accent, borderRadius: 4 }} />
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                  <Text style={{ fontSize: 10, color: theme.colors.textTertiary }}>You</Text>
                  <Text style={{ fontSize: 10, color: theme.colors.textTertiary }}>{opponentName}</Text>
                </View>
              </View>
            );
          })()}

          {/* Expired banner */}
          {isExpired && (
            <View style={[styles.expiredBanner, { backgroundColor: '#ef444418', borderColor: '#ef444440' }]}>
              <Text style={styles.expiredText}>EXPIRED — No Winner</Text>
              <TouchableOpacity
                style={[styles.dismissBtn, { backgroundColor: theme.colors.surfaceVariant }]}
                onPress={onClose}
              >
                <Text style={[styles.dismissBtnText, { color: theme.colors.text }]}>Dismiss</Text>
              </TouchableOpacity>
            </View>
          )}

          {/* Live progress for active */}
          {isActive && (
            <LiveDuelProgress duel={duel} currentUserId={currentUserId} />
          )}

          {/* How to win is now condensed inside LiveDuelProgress card */}

          {/* Log a Lift CTA is inside LiveDuelProgress card above */}

          {/* Live activity feed is now inside LiveDuelProgress card */}

          {/* Lift history */}
          <View style={styles.section}>
            <Text style={[styles.sectionTitle, { color: theme.colors.textSecondary }]}>
              LIFT HISTORY
            </Text>
            {loading ? (
              <ActivityIndicator style={{ marginVertical: 20 }} color={theme.colors.accent} />
            ) : lifts.length === 0 ? (
              <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
                No lifts recorded yet
              </Text>
            ) : (
              lifts.map((lift) => {
                const isYou = lift.profile_id === currentUserId;
                return (
                  <View
                    key={lift.id}
                    style={[styles.liftRow, { backgroundColor: theme.colors.surfaceVariant }]}>
                    <Text style={[styles.liftUser, { color: isYou ? theme.colors.accent : theme.colors.text }]}>
                      {isYou ? 'You' : opponentName}
                    </Text>
                    <Text style={[styles.liftWeight, { color: theme.colors.text }]}>
                      {Math.round(displayWeight(lift.weight, 'lbs'))} {unitLabel} × {lift.reps}
                    </Text>
                    <Text style={[styles.liftTime, { color: theme.colors.textSecondary }]}>
                      {new Date(lift.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Text>
                  </View>
                );
              })
            )}
          </View>

          {/* Share button for active duels */}
          {isActive && (
            <TouchableOpacity
              style={[styles.shareBtn, { borderColor: theme.colors.border }]}
              onPress={async () => {
                if (!duel?.id) return;
                const webLink = generateDuelWebLink(duel.id);
                const shareMsg = `Check out this duel on DUEL! ${webLink}`;
                try {
                  if (Platform.OS === 'web') {
                    if (typeof navigator !== 'undefined' && navigator.share) {
                      await navigator.share({ title: 'DUEL', text: shareMsg, url: webLink });
                    } else {
                      await Clipboard.setStringAsync(webLink);
                      if (typeof window !== 'undefined') window.alert('Duel link copied to clipboard!');
                    }
                  } else {
                    await Share.share({ message: shareMsg, url: webLink });
                  }
                } catch { /* user cancelled */ }
              }}
            >
              <Share2 size={18} color={theme.colors.textSecondary} />
              <Text style={[styles.shareBtnText, { color: theme.colors.textSecondary }]}>Share Duel</Text>
            </TouchableOpacity>
          )}

          {/* Matchmaking rationale */}
          <View style={[styles.rationaleBox, { backgroundColor: theme.colors.surfaceVariant }]}>
            <Text style={[styles.rationaleTitle, { color: theme.colors.textSecondary }]}>
              Why this opponent?
            </Text>
            <Text style={[styles.rationaleText, { color: theme.colors.textSecondary }]}>
              Matched based on similar strength levels and gym activity to ensure competitive duels.
            </Text>
          </View>

          {/* Actions for completed */}
          {isCompleted && opponentId && duel.exercise_id && (
            <View style={styles.actionRow}>
              <RematchButton
                opponentId={opponentId}
                exerciseId={duel.exercise_id}
                timeframeHours={duel.timeframe_hours}
                duelType={duel.duel_type}
                onRematchCreated={onClose}
              />
              <TouchableOpacity
                style={[styles.shareBtn, { borderColor: theme.colors.border }]}
                onPress={async () => {
                  if (!duel?.id) return;
                  const webLink = generateDuelWebLink(duel.id);
                  const message = getDuelShareText(duel.id, !!isWinner);
                  try {
                    if (Platform.OS === 'web') {
                      if (typeof navigator !== 'undefined' && navigator.share) {
                        await navigator.share({ title: 'DUEL', text: message, url: webLink });
                      } else {
                        await Clipboard.setStringAsync(webLink);
                        if (typeof window !== 'undefined') window.alert('Duel link copied to clipboard!');
                      }
                    } else {
                      await Share.share({ message, url: webLink });
                    }
                  } catch { /* user cancelled */ }
                }}>
                <Share2 size={18} color={theme.colors.textSecondary} />
                <Text style={[styles.shareBtnText, { color: theme.colors.textSecondary }]}>Share</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.shareBtn, { borderColor: '#3B82F6', backgroundColor: 'rgba(59,130,246,0.1)' }]}
                onPress={() => setShowDuelShareCard(true)}>
                <Share2 size={18} color="#3B82F6" />
                <Text style={[styles.shareBtnText, { color: '#3B82F6' }]}>Share Card</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      {/* Duel Share Card Modal */}
      <Modal visible={showDuelShareCard} animationType="slide" transparent>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.85)', justifyContent: 'flex-end' }}>
          <View style={{ backgroundColor: theme.colors.surface, borderTopLeftRadius: 16, borderTopRightRadius: 16, padding: 20, maxHeight: '80%' }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Text style={{ fontSize: 18, fontWeight: '700', color: theme.colors.text }}>Share Duel Result</Text>
              <TouchableOpacity onPress={() => setShowDuelShareCard(false)} style={{ padding: 4 }}>
                <X size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ alignItems: 'center' }}>
              <View style={{ transform: [{ scale: 0.18 }], width: 1080 * 0.18, height: 1920 * 0.18 }}>
                <DuelShareCard
                  ref={duelCardRef}
                  winner={{
                    username: isWinner ? (duel?.challenger_id === currentUserId ? 'You' : opponentName) : opponentName,
                    score: Math.max(duel?.challenger_score ?? 0, duel?.challenged_score ?? 0),
                  }}
                  loser={{
                    username: isWinner ? opponentName : 'You',
                    score: Math.min(duel?.challenger_score ?? 0, duel?.challenged_score ?? 0),
                  }}
                  exercise={duel?.exercise_name ?? duel?.exercise_id ?? 'Exercise'}
                  duelType={duel?.duel_type ?? 'Volume'}
                  duration={duel?.timeframe_hours ? `${duel.timeframe_hours}h` : '24h'}
                  unit={userUnits}
                />
              </View>
            </ScrollView>
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14, borderRadius: 0, marginTop: 16, backgroundColor: '#3B82F6' }}
              onPress={async () => {
                try { await shareCard(duelCardRef); } catch { /* cancelled */ }
              }}>
              <Share2 size={20} color="#fff" />
              <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700' }}>Share Card</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    paddingTop: 56,
    borderBottomWidth: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
  },
  subtitle: {
    fontSize: 13,
    fontWeight: '600',
    marginTop: 2,
  },
  closeBtn: {
    padding: 8,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
    gap: 16,
  },
  section: {
    gap: 8,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    marginBottom: 4,
  },
  emptyText: {
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 20,
  },
  liftRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderRadius: 2,
    gap: 8,
  },
  liftUser: {
    fontSize: 13,
    fontWeight: '700',
    width: 70,
  },
  liftWeight: {
    fontSize: 14,
    fontWeight: '800',
    flex: 1,
  },
  liftTime: {
    fontSize: 12,
    fontWeight: '500',
  },
  rationaleBox: {
    padding: 14,
    borderRadius: 2,
  },
  rationaleTitle: {
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 4,
  },
  rationaleText: {
    fontSize: 13,
    lineHeight: 18,
  },
  actionRow: {
    flexDirection: 'row',
    gap: 12,
  },
  shareBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 0,
    borderWidth: 1,
  },
  logLiftBtn: {
    paddingVertical: 14,
    borderRadius: 0,
    alignItems: 'center',
  },
  logLiftBtnText: {
    fontSize: 16,
    fontWeight: '800',
  },
  shareBtnText: {
    fontSize: 15,
    fontWeight: '600',
  },
  expiredBanner: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    alignItems: 'center',
    gap: 10,
  },
  expiredText: {
    color: '#ef4444',
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: 1,
  },
  dismissBtn: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 0,
  },
  dismissBtnText: {
    fontSize: 14,
    fontWeight: '700',
  },
});
